<script setup lang="ts">
import type { CreateVisitorLogForm, UpdateVisitorLogForm } from '@/types/hvl/forms'
import { createVisitorLog, updateVisitorLog } from '@/services/hvl.service'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import { vMaska } from 'maska/vue'
import { reactive, ref } from 'vue'
import type { VisitorLog } from '@/types/hvl/VisitorLog'
import type { LiteSite } from '@/types/core/Site'
import { DateTime } from 'luxon'

const props = defineProps<{
  visitorLog?: VisitorLog
  sites: LiteSite[]
  defaultSite?: string
}>()

const photoIDTypeOptions = [
  {
    key: 'Drivers License',
    value: 'Drivers License',
  },
  {
    key: 'Military ID',
    value: 'Military ID',
  },
  {
    key: 'Passport',
    value: 'Passport',
  },
]

const natureOfVisitOptions = [
  {
    key: 'Contractor',
    value: 'Contractor',
  },
  {
    key: 'Employee',
    value: 'Employee',
  },
  {
    key: 'Training',
    value: 'Training',
  },
  {
    key: 'Vendor',
    value: 'Vendor',
  },
  {
    key: 'Visitor',
    value: 'Visitor',
  },
]

const model = defineModel<boolean>()

const createVisitorLogFormRef = ref<FormInstance>()
const createVisitorLogForm = reactive<CreateVisitorLogForm>({
  site_id: '',
  visit_date: DateTime.now().toISODate(),
  vehicle_license_plate: undefined,
  photo_id_type: undefined,
  photo_id: undefined,
  name: '',
  date_of_birth: undefined,
  company: undefined,
  email_address: '',
  phone_number: '',
  nature_of_visit: '',
  authorized: false,
  sign_in: undefined,
  sign_out: undefined,
})

const rules = reactive<FormRules<CreateVisitorLogForm>>({
  site_id: [{ required: true, message: 'Site is required.' }],
  visit_date: [{ required: true, message: 'Visit Date is required.' }],
  name: [{ required: true, message: 'Name is required.' }],
  nature_of_visit: [{ required: true, message: 'Nature of Visit is required.' }],
  email_address: [{ type: 'email', required: true, message: 'A valid email is required.', trigger: 'blur' }],
  phone_number: [
    {
      required: true,
      message: 'Phone number is required.',
    },
    {
      type: 'pattern',
      pattern: /^\+?\d{2,}$/,
      message: 'Phone number must be numeric.',
      trigger: 'blur',
    },
    {
      type: 'pattern',
      pattern: /^\+/,
      message: 'Phone number must include country code.',
      trigger: 'blur',
    },
  ],
  sign_in: [
    {
      validator: (rule, value, callback) => {
        // Validate that sign in time is required if visitor is authorized
        if (createVisitorLogForm.authorized && !value) {
          return callback(new Error('Sign In time is required for authorized visitors.'))
        }

        const sign_in_datetime = DateTime.fromISO(value)

        // Validate sign in time is not a future date
        if (sign_in_datetime > DateTime.now()) {
          return callback(new Error('Sign In time cannot be a future date.'))
        }

        // Validate sign in time date matches the visit date
        if (sign_in_datetime.toISODate() !== DateTime.fromISO(createVisitorLogForm.visit_date).toISODate()) {
          return callback(new Error('Sign In date must be equal to visit date'))
        }
        callback()
      },
      trigger: 'blur',
    },
  ],
  sign_out: [
    {
      validator: (rule, value, callback) => {
        if (!createVisitorLogForm.sign_in && createVisitorLogForm.sign_out) {
          return callback(new Error('Sign In time is required when Sign Out time is ingressed.'))
        }
        if (
          createVisitorLogForm.sign_out &&
          createVisitorLogForm.sign_in &&
          createVisitorLogForm.sign_in >= createVisitorLogForm.sign_out
        ) {
          return callback(new Error('Sign Out time must be after Sign In time.'))
        }
        callback()
      },
      trigger: 'blur',
    },
  ],
  date_of_birth: [
    {
      validator: (rule, value, callback) => {
        if (!value) {
          return callback()
        }

        const date_of_birth_date = DateTime.fromISO(value).toISODate()

        if (date_of_birth_date && date_of_birth_date >= DateTime.now().toISODate()) {
          return callback(new Error('Date of birth must be in the past.'))
        }
        callback()
      },
      trigger: 'blur',
    },
  ],
})

const queryClient = useQueryClient()

const create = useMutation({
  mutationFn: (form: CreateVisitorLogForm) => createVisitorLog(form),
  onSuccess: () => queryClient.resetQueries({ queryKey: ['visitor-logs'] }),
})

const update = useMutation({
  mutationFn: (form: UpdateVisitorLogForm) => updateVisitorLog(form),
  onSuccess: () => queryClient.resetQueries({ queryKey: ['visitor-logs'] }),
})

const initForm = async () => {
  if (!props.visitorLog) {
    // Log creation
    createVisitorLogForm.site_id = props.defaultSite ?? ''
    createVisitorLogForm.sign_in = DateTime.now().toISO()
    createVisitorLogForm.visit_date = DateTime.now().toISODate()
    return
  }

  // Log update
  createVisitorLogForm.site_id = props.visitorLog.site_id
  createVisitorLogForm.visit_date = props.visitorLog.visit_date
  createVisitorLogForm.vehicle_license_plate = props.visitorLog.vehicle_license_plate ?? undefined
  createVisitorLogForm.photo_id_type = props.visitorLog.photo_id_type ?? undefined
  createVisitorLogForm.photo_id = props.visitorLog.photo_id ?? undefined
  createVisitorLogForm.name = props.visitorLog.name
  createVisitorLogForm.date_of_birth = props.visitorLog.date_of_birth ?? undefined
  createVisitorLogForm.company = props.visitorLog.company ?? undefined
  createVisitorLogForm.email_address = props.visitorLog.email_address
  createVisitorLogForm.phone_number = props.visitorLog.phone_number
  createVisitorLogForm.nature_of_visit = props.visitorLog.nature_of_visit
  createVisitorLogForm.authorized = props.visitorLog.authorized
  createVisitorLogForm.sign_in = props.visitorLog.sign_in
    ? (DateTime.fromISO(props.visitorLog.sign_in).toISO() ?? undefined)
    : undefined
  createVisitorLogForm.sign_out = props.visitorLog.sign_out
    ? (DateTime.fromISO(props.visitorLog.sign_out).toISO() ?? undefined)
    : undefined
}

/**
 * Handles form submit.
 *
 * @param formEl form element ref.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  formEl?.validate(async (valid, fields) => {
    if (valid) {
      try {
        let msg: string = ''

        if (props.visitorLog) {
          const updateForm: UpdateVisitorLogForm = {
            id: props.visitorLog.id!,
            ...createVisitorLogForm,
          }
          await update.mutateAsync(updateForm)
          msg = 'Log updated successfully'
        } else {
          await create.mutateAsync(createVisitorLogForm)
          msg = 'Log created successfully'
        }

        ElMessage({
          type: 'success',
          message: msg,
        })
        resetFormCloseDialog()
      } catch (err: any) {
        ElMessage({
          type: 'error',
          message: `Operation failed: ${err.message}`,
        })
      }
    }
  })
}

/**
 * Resets form, closes dialog.
 */
const resetFormCloseDialog = () => {
  resetForm(createVisitorLogFormRef.value)
  model.value = false
}

/**
 * Resets the form.
 *
 * @param formEl form element ref.
 */
const resetForm = (formEl: FormInstance | undefined) => {
  formEl?.resetFields()
}
</script>

<template>
  <el-dialog
    v-model="model"
    :title="`${visitorLog ? 'Update' : 'Create'} Log`"
    :show-close="false"
    :width="480"
    @open="initForm"
    @closed="resetFormCloseDialog"
  >
    <el-form
      ref="createVisitorLogFormRef"
      :model="createVisitorLogForm"
      :rules="rules"
      label-width="auto"
      label-position="left"
    >
      <el-form-item label="Site" prop="site_id">
        <el-select v-model="createVisitorLogForm.site_id" placeholder="Site" clearable>
          <el-option v-for="s in props.sites" :key="s.site_name" :label="s.site_name" :value="s.site_id" />
        </el-select>
      </el-form-item>
      <el-form-item label="Visit Date" prop="visit_date">
        <el-date-picker
          v-model="createVisitorLogForm.visit_date"
          type="date"
          placeholder="Pick a day"
          format="MM/DD/YYYY"
          value-format="YYYY-MM-DD"
        />
      </el-form-item>
      <el-form-item label="Vehicle License Plate" prop="vehicle_license_plate">
        <el-input v-model="createVisitorLogForm.vehicle_license_plate" placeholder="License Plate" clearable />
      </el-form-item>
      <el-form-item label="Photo ID Type" prop="photo_id_type">
        <el-select v-model="createVisitorLogForm.photo_id_type" placeholder="Photo ID Type" clearable>
          <el-option v-for="item in photoIDTypeOptions" :key="item.key" :label="item.value" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="Photo ID #" prop="photo_id">
        <el-input v-model="createVisitorLogForm.photo_id" placeholder="Photo ID #" clearable />
      </el-form-item>
      <el-form-item label="Name" prop="name">
        <el-input v-model="createVisitorLogForm.name" placeholder="Name" clearable />
      </el-form-item>
      <el-form-item label="Date of Birth" prop="date_of_birth">
        <el-date-picker
          v-model="createVisitorLogForm.date_of_birth"
          type="date"
          placeholder="Pick a day"
          format="MM/DD/YYYY"
          value-format="YYYY-MM-DD"
        />
      </el-form-item>
      <el-form-item label="Company" prop="company">
        <el-input v-model="createVisitorLogForm.company" placeholder="Company" clearable />
      </el-form-item>
      <el-form-item label="Email" prop="email_address">
        <el-input
          v-model="createVisitorLogForm.email_address"
          type="email"
          required
          placeholder="crypto.miner@example.com"
        />
      </el-form-item>
      <el-form-item label="Phone" prop="phone_number">
        <div class="phone-input">
          <el-input
            v-model="createVisitorLogForm.phone_number"
            v-maska
            type="tel"
            data-maska="+9"
            data-maska-tokens="9:[0-9]:repeated"
            placeholder="+14849994949"
            clearable
          />
        </div>
      </el-form-item>
      <el-form-item label="Nature of Visit" prop="nature_of_visit">
        <el-select v-model="createVisitorLogForm.nature_of_visit" placeholder="Nature of Visit" clearable>
          <el-option v-for="item in natureOfVisitOptions" :key="item.key" :label="item.value" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="Authorized" prop="authorized">
        <el-switch v-model="createVisitorLogForm.authorized" />
      </el-form-item>
      <el-form-item v-if="createVisitorLogForm.authorized" label="Sign In" prop="sign_in">
        <el-date-picker
          v-model="createVisitorLogForm.sign_in"
          type="datetime"
          placeholder="Sign In time"
          value-format="YYYY-MM-DDTHH:mm:ss.SSSZ"
        />
      </el-form-item>
      <el-form-item v-if="createVisitorLogForm.authorized" label="Sign Out" prop="sign_out">
        <el-date-picker
          v-model="createVisitorLogForm.sign_out"
          type="datetime"
          placeholder="Sign Out time"
          value-format="YYYY-MM-DDTHH:mm:ss.SSSZ"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="resetFormCloseDialog">Cancel</el-button>
      <el-button type="primary" @click="submitForm(createVisitorLogFormRef)">Submit</el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.phone-input {
  width: 100%;
  display: inline-flex;
  gap: var(--spacing-spacing-sm);

  .el-select {
    width: 112px;
  }
}
</style>
